// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

/**
 * Centralized Scenario Configuration
 *
 * This file contains all scenario-related configurations used across the application.
 * Update values here to reflect changes in the database schema.
 */

export const SCENARIOS = {
  // Market scenarios
  MARKET_SMALL: "market_small",
  MARKET_MEDIUM: "market_medium",
  MARKET_LARGE: "market_large",

  // Info scenarios
  INFO_SMALL: "info_small",
  INFO_MEDIUM: "info_medium",
  INFO_LARGE: "info_large",

  // Culture scenarios
  CULTURE_SMALL: "culture_small",
  CULTURE_MEDIUM: "culture_medium",
  CULTURE_LARGE: "culture_large",

  // Flexible scenarios
  FLEXIBLE_SMALL: "flexible_small",
  FLEXIBLE_MEDIUM: "flexible_medium",
  FLEXIBLE_LARGE: "flexible_large",

  // Delivery
  DELIVERY: "delivery",

  // Infrastructure scenarios
  PARKING: "parking",
  RADBOX: "radbox",
  INFRA: "infra",

  // Special cases
  NO_PACKAGE: "no_package",
  NO_SCENARIO: "no_scenario",
  NONE: "none",
};

export const SCENARIO_TRANSLATIONS = {
  [SCENARIOS.MARKET_SMALL]: "Markt klein",
  [SCENARIOS.MARKET_MEDIUM]: "Markt mittel",
  [SCENARIOS.MARKET_LARGE]: "Markt groß",

  [SCENARIOS.INFO_SMALL]: "Info klein",
  [SCENARIOS.INFO_MEDIUM]: "Info mittel",
  [SCENARIOS.INFO_LARGE]: "Info groß",

  [SCENARIOS.CULTURE_SMALL]: "Kultur klein",
  [SCENARIOS.CULTURE_MEDIUM]: "Kultur mittel",
  [SCENARIOS.CULTURE_LARGE]: "Kultur groß",

  [SCENARIOS.FLEXIBLE_SMALL]: "Flexibel klein",
  [SCENARIOS.FLEXIBLE_MEDIUM]: "Flexibel mittel",
  [SCENARIOS.FLEXIBLE_LARGE]: "Flexibel groß",

  [SCENARIOS.DELIVERY]: "Lieferung",

  [SCENARIOS.PARKING]: "Parkplatz",
  [SCENARIOS.RADBOX]: "Radbox",
  [SCENARIOS.INFRA]: "Infrastruktur",

  [SCENARIOS.NO_PACKAGE]: "Kein Paket",
  [SCENARIOS.NO_SCENARIO]: "Kein Szenario",
  [SCENARIOS.NONE]: "Keine",
};

export const SCENARIO_SHORT_TRANSLATIONS = {
  [SCENARIOS.MARKET_SMALL]: "Markt klein",
  [SCENARIOS.MARKET_MEDIUM]: "Markt mittel",
  [SCENARIOS.MARKET_LARGE]: "Markt groß",

  [SCENARIOS.INFO_SMALL]: "Info klein",
  [SCENARIOS.INFO_MEDIUM]: "Info mittel",
  [SCENARIOS.INFO_LARGE]: "Info groß",

  [SCENARIOS.CULTURE_SMALL]: "Kultur klein",
  [SCENARIOS.CULTURE_MEDIUM]: "Kultur mittel",
  [SCENARIOS.CULTURE_LARGE]: "Kultur groß",

  [SCENARIOS.FLEXIBLE_SMALL]: "Flexibel klein",
  [SCENARIOS.FLEXIBLE_MEDIUM]: "Flexibel mittel",
  [SCENARIOS.FLEXIBLE_LARGE]: "Flexibel groß",

  [SCENARIOS.DELIVERY]: "Lieferung",

  [SCENARIOS.PARKING]: "Parkplatz",
  [SCENARIOS.RADBOX]: "Radbox",
  [SCENARIOS.INFRA]: "Infrastruktur",

  [SCENARIOS.NO_PACKAGE]: "Kein Paket",
  [SCENARIOS.NO_SCENARIO]: "Kein Szenario",
  [SCENARIOS.NONE]: "Keine",
};

// Scenario options for dropdowns (used in forms)
export const SCENARIO_OPTIONS = [
  {
    value: SCENARIOS.MARKET_SMALL,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.MARKET_SMALL],
  },
  {
    value: SCENARIOS.MARKET_MEDIUM,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.MARKET_MEDIUM],
  },
  {
    value: SCENARIOS.MARKET_LARGE,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.MARKET_LARGE],
  },

  {
    value: SCENARIOS.INFO_SMALL,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.INFO_SMALL],
  },
  {
    value: SCENARIOS.INFO_MEDIUM,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.INFO_MEDIUM],
  },
  {
    value: SCENARIOS.INFO_LARGE,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.INFO_LARGE],
  },

  {
    value: SCENARIOS.CULTURE_SMALL,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.CULTURE_SMALL],
  },
  {
    value: SCENARIOS.CULTURE_MEDIUM,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.CULTURE_MEDIUM],
  },
  {
    value: SCENARIOS.CULTURE_LARGE,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.CULTURE_LARGE],
  },

  {
    value: SCENARIOS.FLEXIBLE_SMALL,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.FLEXIBLE_SMALL],
  },
  {
    value: SCENARIOS.FLEXIBLE_MEDIUM,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.FLEXIBLE_MEDIUM],
  },
  {
    value: SCENARIOS.FLEXIBLE_LARGE,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.FLEXIBLE_LARGE],
  },

  {
    value: SCENARIOS.DELIVERY,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.DELIVERY],
  },

  {
    value: SCENARIOS.PARKING,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.PARKING],
  },
  {
    value: SCENARIOS.RADBOX,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.RADBOX],
  },
  {
    value: SCENARIOS.INFRA,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.INFRA],
  },

  {
    value: SCENARIOS.NO_PACKAGE,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.NO_PACKAGE],
  },
  {
    value: SCENARIOS.NO_SCENARIO,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.NO_SCENARIO],
  },
  {
    value: SCENARIOS.NONE,
    label: SCENARIO_SHORT_TRANSLATIONS[SCENARIOS.NONE],
  },
];

export const SVG_GROUP_TEMPLATE = [
  ["scenario_culture_large", "space_culture_large"],
  ["scenario_culture_medium", "space_culture_medium"],
  ["scenario_culture_small", "space_culture_small"],

  ["scenario_info_large", "space_info_large"],
  ["scenario_info_medium", "space_info_medium"],
  ["scenario_info_small", "space_info_small"],

  ["scenario_market_large", "space_market_large"],
  ["scenario_market_medium", "space_market_medium"],
  ["scenario_market_small", "space_market_small"],

  ["scenario_delivery", "space_delivery"],

  ["scenario_parking", "space_parking"],
  ["scenario_radbox", "space_radbox"],
  ["scenario_infra", "space_infra"],
];

export const getScenarioLabel = (scenarioKey, useShort = false) => {
  const translations = useShort
    ? SCENARIO_SHORT_TRANSLATIONS
    : SCENARIO_TRANSLATIONS;
  return translations[scenarioKey] || scenarioKey;
};

export const isDeliveryScenario = (scenarioKey) => {
  return scenarioKey && scenarioKey.startsWith("delivery_");
};

export const EVENT_TYPES = {
  MARKET: "market",
  CULTURE: "culture",
  INFO: "info",
  MOBILITY: "mobility",
  CHARGING: "charging",
  DELIVERY: "delivery",
  PARKING: "parking",
  RADBOX: "radbox",
  INFRA: "infra",
  NONE: "none",
};

// Event sizes
export const EVENT_SIZES = {
  NONE: "none",
  SMALL: "small",
  MEDIUM: "medium",
  LARGE: "large",
};

export const PACKAGE_OPTIONS = [
  { value: "S", label: "S - Small" },
  { value: "M", label: "M - Medium" },
  { value: "L", label: "L - Large" },
  { value: "none", label: "None (no package)" },
];

export const LAYOUT_POSITION_OPTIONS = [
  { value: "Pflicht", label: "Pflicht" },
  { value: "Auswahl", label: "Auswahl" },
  { value: "Zusatz", label: "Zusatz" },
  { value: "Service", label: "Service" },
  { value: "Summary", label: "Summary" },
  { value: "Hidden", label: "Hidden" },
  { value: "none", label: "None" },
];

// Mietdauer (rental duration) options
export const MIETDAUER_OPTIONS = [
  { value: "einmalig", label: "Einmalig" },
  { value: "täglich", label: "Täglich" },
  { value: "wöchentlich", label: "Wöchentlich" },
  { value: "monatlich", label: "Monatlich" },
];
